import type { DirectiveBinding, Directive } from 'vue'
import { triggers } from '@/scripts/global-modals-commands'
import { FeedEventSource, MemberEventSource } from '@/utils/segment/segment.types'

/**
 * Handle click event and add css color class to open member side bar
 * @name OpenMemberSideBar
 * @returns {*} picture or text link class
 * @handles openMemberSideBar action
 */

interface OpenMemberSidebarValue {
  patientUuid: string
  masterTaskId?: string | number
  memberStatus?: string
  source?: FeedEventSource | MemberEventSource
  taskListSegmentEvent?: unknown
}

const clickHandlers = new WeakMap()

const createClickHandler = (binding: DirectiveBinding<OpenMemberSidebarValue>) => {
  const { patientUuid: memberUuid, masterTaskId, memberStatus, source, taskListSegmentEvent } = binding.value

  return (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    triggers.toggleMemberSidebar({ memberUuid, masterTaskId, memberStatus, source, taskListSegmentEvent })
  }
}

const openMemberSidebar: Directive = {
  beforeMount(el: HTMLElement, binding: DirectiveBinding<OpenMemberSidebarValue>) {
    const clickHandler = createClickHandler(binding)

    clickHandlers.set(el, clickHandler)
    el.addEventListener('click', clickHandler)
  },
  mounted(el, binding) {
    const customClass = binding.arg === 'picture' ? 'button-link button-link--picture' : 'button-link'

    el.className += ` ${customClass}`
  },
  updated(el, binding) {
    const currentUuid = binding.value?.patientUuid
    const prevUuid = binding.oldValue?.patientUuid

    if (!prevUuid || currentUuid === prevUuid) return

    const oldHandler = clickHandlers.get(el)

    if (oldHandler) el.removeEventListener('click', oldHandler)

    const newClickHandler = createClickHandler(binding)

    clickHandlers.set(el, newClickHandler)
    el.addEventListener('click', newClickHandler)
  },
  unmounted(el) {
    const clickHandler = clickHandlers.get(el)

    if (!clickHandler) return

    el.removeEventListener('click', clickHandler)
    clickHandlers.delete(el)
  },
}

export default openMemberSidebar
